/**
 * ServiceBuilderSection.js - Support at Home "Build Your Service Plan" tab.
 * Service selection, frequency, strategy comparison, insight panel, save/restore.
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Home, AlertCircle, Info, TrendingUp, HelpCircle, Search, Copy, Eye } from 'lucide-react';
import { ACAT_LEVEL_DATA } from '../../../../shared/src/calculation/constants/acatLevelData.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { SUPPORT_AT_HOME_SERVICES } from './supportAtHomeServices.tsx';
import { computeServiceCostsForPerson, splitOutOfPocket } from './serviceBuilderCosts.ts';
import ServiceBuilderInsightPanel from './ServiceBuilderInsightPanel.tsx';
import { ServiceBuilderIntro, OverBudgetHelp, ContributionCashflowNote } from './ServiceBuilderCoach.tsx';
import { getServicePlanWithSource, normaliseStoredServices, setCurrentServicePlan, clearCurrentServicePlanMeta, clearNoServicePlanChoice, listSavedServicePlans, storedPlanHasServices, buildServicePlanRecord } from '../../../ServicePlanUtils.ts';
import { buildWalkInGapLine, accessibleSavingsOf } from '../agedCare/WalkInPositionNote.tsx';
import ProjectionsModal from '../agedCare/ProjectionsModal.tsx';
import { readPeriodMode, type ProjectionsModalPeriodMode } from '../agedCare/projectionsModalPreferences.ts';
import {
    getSavedHomeCareStrategies,
    getSavedAgedCareStrategies,
    resolveInitialServiceBuilderSelectionId,
    setActiveHomeCareStrategyId,
    formatHomeCareStrategyLabel,
    formatAgedCareStrategyLabel,
    formatServiceBuilderSelectionId,
    parseServiceBuilderSelectionId,
    resolveServiceBuilderPosition,
    getServiceBuilderContributionContextOptions,
    HOME_CARE_STRATEGIES_KEY,
    HOME_CARE_STRATEGIES_UPDATED_EVENT,
    clearManualAgedCareLink,
} from './homeCareStrategyStorage.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';
import { notify } from '../../components/ui/notices.tsx';

/** One selected-service line item: quantity typed at a given frequency. */
export interface ServiceBuilderEntry {
    quantity: number | string;
    frequency: 'daily' | 'weekly' | 'monthly' | string;
}

/** { [serviceId]: entry } for one care recipient (client or partner). */
export type ServiceBuilderSideSelections = Record<string, ServiceBuilderEntry | undefined>;

export interface ServiceBuilderSelections {
    client: ServiceBuilderSideSelections;
    partner: ServiceBuilderSideSelections;
    [key: string]: ServiceBuilderSideSelections;
}

export interface ServiceCostsBreakdown {
    totalClinical: number;
    totalIndependence: number;
    totalEveryday: number;
    total: number;
    actualDistribution?: { clinical: number; independence: number; everyday: number };
    contribution: { clinical: number; independence: number; everyday: number; total: number; [key: string]: number };
}

export interface ServiceBuilderContributionRates {
    clinical?: number;
    independence?: number;
    everydayLiving?: number;
    /** The no-worse-off ceiling on a year's contributions, in dollars — null or absent when none applies. */
    annualCap?: number | null;
}

/** Details of the most-recently-changed service selection, used for dynamic insight-panel messaging. */
export interface LastChangedServiceInfo {
    name: string;
    category: string;
    unit: string;
    medianPrice: number;
    serviceId: string;
    quantity: number;
    frequency: string;
    activeTab: 'client' | 'partner';
}

/** Service-selection sub-bag of SavedStrategy.inputs.services — StrategyIntegrationUtils.ts's SavedStrategy
 * type keeps `inputs`/`outputs` as free-form bags (`[key: string]: unknown`), so this narrower local shape
 * covers the specific `services`/`serviceDistribution` fields this component reads off a saved strategy. */
export interface SavedStrategyServicesShape {
    serviceDistribution?: { clinical?: number; independence?: number; everyday?: number };
    services?: Record<string, unknown>;
    client?: ServiceBuilderSideSelections;
    partner?: ServiceBuilderSideSelections | null;
    [key: string]: unknown;
}

/** Resolved financial position (baseline / linked home-care strategy / linked aged-care strategy). Shapes are
 * ad hoc bags coming from homeCareStrategyStorage.tsx and calculationApi.ts (not yet modelled with shared types). */
export interface ServiceBuilderPosition {
    kind: 'baseline' | 'home_care' | 'aged_care';
    strategy: SavedStrategy | null;
    integration: Record<string, unknown> | null;
    financials: Record<string, unknown> | null;
    hasLinkedAgedCare: boolean;
}

export interface ServiceBuilderSectionProps {
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    onNext: () => void;
    onBack: () => void;
    setCurrentSection?: (section: string) => void;
    setActivePage?: (page: string) => void;
    /**
     * When support services begin — asked HERE because it is a property of the service plan.
     *
     * It used to be asked on Optimise Your Plan, one screen after the place it belongs, which put a
     * care fact among the financial levers. Optimise still needs the value (it is the date the
     * Suggestion Engine projects every candidate from) so it reads it from the page rather than
     * asking again: two inputs for one fact is how two screens come to disagree.
     */
    supportStartDate?: string;
    setSupportStartDate?: (value: string) => void;
}

// Service Builder Section
const ServiceBuilderSection = ({ factFind, setFactFind, onNext, onBack, supportStartDate = '', setSupportStartDate }: ServiceBuilderSectionProps) => {
    // Read restored service plan once on mount
    const restoredServicePlan: { name?: string; inputs?: { services?: ServiceBuilderSideSelections & { client?: ServiceBuilderSideSelections; partner?: ServiceBuilderSideSelections } } } | null = (() => {
        try {
            const stored = typeof window !== 'undefined' ? sessionStorage.getItem('careCompanionRestoreServicePlan') : null;
            if (stored) {
                const strategy = JSON.parse(stored);
                sessionStorage.removeItem('careCompanionRestoreServicePlan');
                return strategy;
            }
        } catch (error) {
            console.error('Error restoring service plan:', error);
        }
        return null;
    })();

    /*
     * SAVED PLANS, VISIBLE HERE (owner-reported, 2026-08-31: "I can't see this saved plan unless I
     * go back to dashboard").
     *
     * Saving wrote to sessionStorage and said nothing more, so the only place a plan appeared was
     * the Dashboard — a client who saved two plans to compare them had to leave the builder to find
     * out whether either had saved at all. Aged Care lists its saved strategies directly under its
     * save box; this is the same treatment.
     *
     * Held in state rather than read on every render so the list updates the instant a plan is
     * saved, which is the moment the reassurance is actually needed.
     */
    const [savedPlans, setSavedPlans] = useState(() => listSavedServicePlans());
    const [activePlanId, setActivePlanId] = useState<string | null>(null);

    const [selectedServices, setSelectedServices] = useState<ServiceBuilderSelections>(() => {
        // Restore selectedServices from a dashboard reopen first...
        const restoredServices = restoredServicePlan?.inputs?.services;
        if (restoredServices) {
            return normaliseStoredServices(restoredServices) as unknown as ServiceBuilderSelections;
        }
        /*
         * ...otherwise seed from the SHARED plan resolution (current session plan, else the newest
         * saved plan). Before this, a plain remount started EMPTY while the dropdown still named
         * the loaded plan and the Optimise tab still priced its services — the owner watched
         * "Service Plan B" selected over a blank grid (2026-08-25). The grid must always show the
         * plan the rest of the app is using.
         */
        const resolved = getServicePlanWithSource();
        if (resolved.source.kind !== 'none') {
            if (resolved.source.kind === 'saved' || resolved.source.kind === 'restore') {
                // Remember the name so the Optimise tab's provenance line stays truthful.
                setCurrentServicePlan(resolved.plan, resolved.source.name);
            }
            return normaliseStoredServices(resolved.plan) as unknown as ServiceBuilderSelections;
        }
        return { client: {}, partner: {} };
    });
    
    // Store selectedServices in sessionStorage whenever it changes (for suggestion engine to access).
    //
    // BUT never let a fresh, untouched mount clobber a plan that's already stored: this effect fires
    // once on mount with `{client:{}, partner:{}}`, and an empty-but-present "current plan" masks the
    // saved-plans fallback in getServicePlanFromStorage — which is how "Optimise Your Plan" came to
    // say "No Service Plan Selected" to a user who had built and saved one. Writing resumes the
    // moment the user touches the plan (including deliberately clearing it), or when a restore
    // seeded it.
    const planTouchedThisVisit = useRef<boolean>(Boolean(restoredServicePlan));
    const planHasAnyService = (sel: ServiceBuilderSelections): boolean =>
        (['client', 'partner'] as const).some((side) =>
            Object.values(sel?.[side] || {}).some((d) => Number((d as { quantity?: number } | undefined)?.quantity) > 0)
        );
    useEffect(() => {
        if (typeof window !== 'undefined' && selectedServices && (planTouchedThisVisit.current || planHasAnyService(selectedServices))) {
            try {
                sessionStorage.setItem('careCompanionCurrentServicePlan', JSON.stringify(selectedServices));
                // Choosing services again is the end of "no service plan" (ServicePlanUtils).
                clearNoServicePlanChoice();
            } catch (error) {
                console.error('Error storing service plan:', error);
            }
        }
    }, [selectedServices]);
    
    const [activeTab, setActiveTab] = useState<'client' | 'partner'>('client'); // 'client' or 'partner'
    const [searchQuery, setSearchQuery] = useState('');
    const [planName, setPlanName] = useState(restoredServicePlan?.name || '');
    const [lastChangedService, setLastChangedService] = useState<LastChangedServiceInfo | null>(null); // Track last service changed for dynamic messaging
    const [selectedStrategyId, setSelectedStrategyId] = useState<string>(() =>
        resolveInitialServiceBuilderSelectionId()
    );
    const [availableHomeCareStrategies, setAvailableHomeCareStrategies] = useState<SavedStrategy[]>(() => getSavedHomeCareStrategies());
    const [availableAgedCareStrategies, setAvailableAgedCareStrategies] = useState<SavedStrategy[]>(() => getSavedAgedCareStrategies());
    const [showServicePlanSavedModal, setShowServicePlanSavedModal] = useState(false); // Modal after saving service plan
    const isPartnered = factFind?.adviceFor === 'couple';

    const [serviceBuilderPosition, setServiceBuilderPosition] = useState<ServiceBuilderPosition>({
        kind: 'baseline', strategy: null, integration: null, financials: null, hasLinkedAgedCare: false,
    });
    useEffect(() => {
        let cancelled = false;
        resolveServiceBuilderPosition(
            selectedStrategyId,
            factFind,
            availableHomeCareStrategies,
            availableAgedCareStrategies
        ).then((r) => { if (!cancelled) setServiceBuilderPosition(r as ServiceBuilderPosition); });
        return () => { cancelled = true; };
    }, [selectedStrategyId, factFind, availableHomeCareStrategies, availableAgedCareStrategies]);

    const hasLinkedAgedCareStrategy = serviceBuilderPosition.hasLinkedAgedCare;

    const selectionStillValid = (selectionId: string | null | undefined, homeCare: SavedStrategy[], agedCare: SavedStrategy[]): boolean => {
        if (!selectionId || selectionId === 'baseline') return true;
        const { kind, rawId } = parseServiceBuilderSelectionId(selectionId);
        if (kind === 'home_care') return homeCare.some((s) => s.id === rawId);
        if (kind === 'aged_care') return agedCare.some((s) => s.id === rawId);
        return false;
    };

    // Load saved strategies (home_care + aged_care) and keep in sync with other modules
    useEffect(() => {
        const refreshStrategies = () => {
            const homeCareStrategies = getSavedHomeCareStrategies();
            const agedCareStrategies = getSavedAgedCareStrategies();
            setAvailableHomeCareStrategies(homeCareStrategies);
            setAvailableAgedCareStrategies(agedCareStrategies);
            setSelectedStrategyId((prev) => {
                if (selectionStillValid(prev, homeCareStrategies, agedCareStrategies)) {
                    return prev;
                }
                return resolveInitialServiceBuilderSelectionId(homeCareStrategies, agedCareStrategies);
            });
        };

        refreshStrategies();

        const handleStorage = (event: StorageEvent | undefined) => {
            if (!event || event.key === HOME_CARE_STRATEGIES_KEY || !event.key) {
                refreshStrategies();
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, refreshStrategies);
            window.addEventListener('storage', handleStorage);
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, refreshStrategies);
                window.removeEventListener('storage', handleStorage);
            }
        };
    }, []);

    // Keep Optimise Your Plan / contribution context aligned when an aged care scenario is selected here
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (serviceBuilderPosition.kind === 'aged_care' && serviceBuilderPosition.strategy) {
            window.sessionStorage.setItem(
                'careCompanionSelectedAgedCareStrategy',
                JSON.stringify(serviceBuilderPosition.strategy)
            );
        } else if (
            serviceBuilderPosition.kind === 'home_care'
            && !serviceBuilderPosition.hasLinkedAgedCare
        ) {
            window.sessionStorage.removeItem('careCompanionSelectedAgedCareStrategy');
        }
    }, [serviceBuilderPosition]);
    
    // Get client's ACAT level and budget. When an aged care strategy is linked, the client is in residential care so their SAH budget is not available.
    // `.level` on the AcatPerson itself (as opposed to `.recommendation.level`) isn't part of the fact-find
    // schema (factFindTypes.ts's AcatPerson has `homeCareLevel`, not `level`) — this is a legacy/dead fallback
    // read, kept for behavioural parity via an explicit cast rather than widening the shared type.
    const clientLevel = factFind?.careNeeds?.clientAcat?.recommendation?.level
        || (factFind?.careNeeds?.clientAcat as { level?: string } | undefined)?.level
        || 'sah_level_6';
    const partnerLevel = factFind?.careNeeds?.partnerAcat?.recommendation?.level
        || (factFind?.careNeeds?.partnerAcat as { level?: string } | undefined)?.level
        || 'sah_level_2';
    const clientBudgetRaw = ACAT_LEVEL_DATA[clientLevel]?.budget || 0;
    const partnerBudgetRaw = isPartnered ? (ACAT_LEVEL_DATA[partnerLevel]?.budget || 0) : 0;
    const clientBudget = hasLinkedAgedCareStrategy ? 0 : clientBudgetRaw;
    const partnerBudget = partnerBudgetRaw;
    const totalBudget = clientBudget + partnerBudget;
    
    // State to trigger recalculation when optimized financials are updated
    const [optimizedFinancialsVersion, setOptimizedFinancialsVersion] = useState(0);
    
    // Listen for changes to optimized financials in sessionStorage
    useEffect(() => {
        const handleStorageChange = (e: Event) => {
            const key = (e as StorageEvent).key;
            if (key === 'careCompanionOptimizedFinancials' || !key) {
                // Trigger recalculation when optimized financials are updated
                setOptimizedFinancialsVersion(prev => prev + 1);
            }
        };
        
        if (typeof window !== 'undefined') {
            window.addEventListener('storage', handleStorageChange);
            // Also listen for custom events (for same-tab updates)
            window.addEventListener('optimizedFinancialsUpdated', handleStorageChange);
        }
        
        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('storage', handleStorageChange);
                window.removeEventListener('optimizedFinancialsUpdated', handleStorageChange);
            }
        };
    }, []);
    
    const [optimizedFinancials, setOptimizedFinancials] = useState<Record<string, unknown>>({});
    useEffect(() => {
        if (serviceBuilderPosition.financials) {
            setOptimizedFinancials(serviceBuilderPosition.financials);
            return undefined;
        }
        let cancelled = false;
        calculationApi.calculateFinancials(factFind).then((r) => { if (!cancelled) setOptimizedFinancials(r); });
        return () => { cancelled = true; };
    }, [serviceBuilderPosition, factFind, optimizedFinancialsVersion]);

    const contributionContextOptions = useMemo(
        () => getServiceBuilderContributionContextOptions(factFind, serviceBuilderPosition),
        [factFind, serviceBuilderPosition]
    );

    // Get contribution rates from optimized financials
    const financials = optimizedFinancials;
    interface ContributionDataBranch {
        contributionRates?: ServiceBuilderContributionRates;
        [key: string]: unknown;
    }
    const [contributionData, setContributionData] = useState<{ client?: ContributionDataBranch; combined?: ContributionDataBranch }>({});
    useEffect(() => {
        // Extract NWOP data from factFind (same as ContributionsSection does)
        const clientHcpStatus = factFind?.careNeeds?.clientAcat?.hcpBeforeSep2024 || 'no';
        const defaultParticipantType = clientHcpStatus === 'yes' ? 'nwop' : 'standard';
        const defaultNwopCategory = factFind?.careNeeds?.clientAcat?.hcpCategory || null;
        const defaultMeansDisclosed = true; // Always use disclosed means for service builder
        const defaultPreviousITCF = Number(factFind?.careNeeds?.clientAcat?.previousITCF) || 0;

        let cancelled = false;
        calculationApi.calculateIndividualContributions(
            factFind,
            financials,
            defaultParticipantType,
            defaultNwopCategory,
            defaultMeansDisclosed,
            defaultPreviousITCF,
            0.5,
            contributionContextOptions
        ).then((r) => { if (!cancelled) setContributionData(r); });
        return () => { cancelled = true; };
    }, [financials, factFind, contributionContextOptions]);
    
    // For couples, use combined rates; for singles, use client rates
    const contributionRates: ServiceBuilderContributionRates = isPartnered
        ? (contributionData?.combined?.contributionRates || {})
        : (contributionData?.client?.contributionRates || {});

    // Convert frequency to monthly for calculations
    const convertToMonthly = (quantity: number | string | undefined, freqType: string | undefined): number => {
        const numericQuantity = Number(quantity);
        if (!quantity || numericQuantity <= 0) return 0;
        switch(freqType) {
            case 'daily': return numericQuantity * 30; // Approximate monthly from daily
            case 'weekly': return numericQuantity * 4; // Approximate monthly from weekly
            case 'monthly': return numericQuantity;
            default: return numericQuantity;
        }
    };
    
    // Calculate selected services costs - for single or active tab
    const calculateServiceCosts = useMemo<ServiceCostsBreakdown>(() => {
        let totalClinical = 0;
        let totalIndependence = 0;
        let totalEveryday = 0;

        const servicesToCalculate = isPartnered ? selectedServices[activeTab] : (selectedServices.client || {});

        Object.entries(servicesToCalculate).forEach(([serviceId, data]) => {
            if (!data || !data.quantity || Number(data.quantity) <= 0) return;

            const monthlyQuantity = convertToMonthly(data.quantity, data.frequency || 'monthly');
            
            // Find service in each category
            Object.values(SUPPORT_AT_HOME_SERVICES).forEach(category => {
                const service = category.services.find(s => s.id === serviceId);
                if (service) {
                    const cost = service.medianPrice * monthlyQuantity * 12; // Convert to annual
                    if (category.label === 'Clinical Care') totalClinical += cost;
                    else if (category.label === 'Independence Services') totalIndependence += cost;
                    else if (category.label === 'Everyday Living Services') totalEveryday += cost;
                }
            });
        });
        
        // Calculate actual distribution
        const total = totalClinical + totalIndependence + totalEveryday;
        const actualDistribution = { clinical: 0, independence: 0, everyday: 0 };
        if (total > 0) {
            actualDistribution.clinical = totalClinical / total;
            actualDistribution.independence = totalIndependence / total;
            actualDistribution.everyday = totalEveryday / total;
        }
        
        // Calculate contribution amounts
        const clinicalContribution = 0; // Clinical is 0% contribution
        const independenceContribution = totalIndependence * (contributionRates.independence || 0);
        const everydayContribution = totalEveryday * (contributionRates.everydayLiving || 0);
        const totalContribution = clinicalContribution + independenceContribution + everydayContribution;
        
        return {
            totalClinical,
            totalIndependence,
            totalEveryday,
            total,
            actualDistribution,
            contribution: {
                clinical: 0,
                independence: independenceContribution,
                everyday: everydayContribution,
                total: totalContribution
            }
        };
    }, [selectedServices, contributionRates, activeTab, isPartnered]);
    
    const serviceCosts = calculateServiceCosts;
    
    // For couples, also calculate combined totals
    const combinedCosts = useMemo<ServiceCostsBreakdown>(() => {
        if (!isPartnered) return serviceCosts;

        let totalClinical = 0;
        let totalIndependence = 0;
        let totalEveryday = 0;

        // Calculate for both clients
        (['client', 'partner'] as const).forEach(person => {
            Object.entries(selectedServices[person] || {}).forEach(([serviceId, data]) => {
                if (!data || !data.quantity || Number(data.quantity) <= 0) return;

                const monthlyQuantity = convertToMonthly(data.quantity, data.frequency || 'monthly');
                
                Object.values(SUPPORT_AT_HOME_SERVICES).forEach(category => {
                    const service = category.services.find(s => s.id === serviceId);
                    if (service) {
                        const cost = service.medianPrice * monthlyQuantity * 12;
                        if (category.label === 'Clinical Care') totalClinical += cost;
                        else if (category.label === 'Independence Services') totalIndependence += cost;
                        else if (category.label === 'Everyday Living Services') totalEveryday += cost;
                    }
                });
            });
        });
        
        const total = totalClinical + totalIndependence + totalEveryday;
        const independenceContribution = totalIndependence * (contributionRates.independence || 0);
        const everydayContribution = totalEveryday * (contributionRates.everydayLiving || 0);
        const totalContribution = independenceContribution + everydayContribution;
        
        return {
            totalClinical,
            totalIndependence,
            totalEveryday,
            total,
            contribution: {
                clinical: 0,
                independence: independenceContribution,
                everyday: everydayContribution,
                total: totalContribution
            }
        };
    }, [selectedServices, contributionRates, isPartnered]);
    
    /*
     * The funded/private split (owner question, 2026-08-25): within each person's package the
     * household pays only the contribution; services ABOVE the package stay in the plan and are
     * bought privately at FULL price. Per person, because budgets are not transferable between a
     * couple. See serviceBuilderCosts.ts for the assumption and the pro-rata allocation.
     */
    const outOfPocket = useMemo(() => {
        const clientCosts = computeServiceCostsForPerson(selectedServices.client || {}, contributionRates, SUPPORT_AT_HOME_SERVICES);
        const client = splitOutOfPocket(clientCosts, clientBudget);
        const partnerCosts = isPartnered
            ? computeServiceCostsForPerson(selectedServices.partner || {}, contributionRates, SUPPORT_AT_HOME_SERVICES)
            : null;
        const partner = partnerCosts ? splitOutOfPocket(partnerCosts, partnerBudget) : null;
        return {
            client,
            partner,
            combined: {
                fundedContribution: client.fundedContribution + (partner?.fundedContribution || 0),
                privateExcess: client.privateExcess + (partner?.privateExcess || 0),
                total: client.total + (partner?.total || 0),
            },
        };
    }, [selectedServices, contributionRates, clientBudget, partnerBudget, isPartnered]);
    const activeOutOfPocket = isPartnered ? (activeTab === 'client' ? outOfPocket.client : outOfPocket.partner) : outOfPocket.client;

    // Determine which budget to use for display
    const currentBudget = isPartnered ? (activeTab === 'client' ? clientBudget : partnerBudget) : clientBudget;
    const currentServicesUsed = serviceCosts.total;
    
    // Calculate projections for legacy estimate
    const [projections, setProjections] = useState<Record<string, unknown>[] | null>(null);
    // Full engine view (charts + monthly narrative) of those same rows — the household's projection
    // with the contribution cost of the services chosen in THIS builder.
    const [showProjectionsModal, setShowProjectionsModal] = useState(false);
    /** Projection horizon for the estate figure (owner ask, 2026-08-25) — same control the
     *  Financial Impact panels offer. */
    const [builderLegacyYears, setBuilderLegacyYears] = useState(5);
    /*
     * The engine's cost stub, as a daily figure.
     *
     * `outOfPocket.combined.total` is the household's REAL yearly outlay: the contribution on the
     * funded share PLUS services above the package bought privately at full price. Feeding only the
     * contribution here was the gap the owner found (2026-08-25) — an over-budget plan projected as
     * almost free. Lifted out of the effect below so the calendar-year re-run shares one definition
     * of it rather than a second copy of the reasoning.
     */
    const supportAtHomeCosts = useMemo(() => ({ totalDailyCost: outOfPocket.combined.total / 365 }), [outOfPocket]);
    /*
     * THE FINANCIAL-YEAR / CALENDAR-YEAR TOGGLE (owner, 23 Sep 2026: present in Aged Care, missing
     * everywhere else). Not a display filter — the engine PROJECTS the year it REPORTS, so the rows
     * are cut again by a second run, which is exactly what the shared modal gates the toggle on.
     * Fetched only while the modal is open, so a remembered 'calendar_year' does not fire a second
     * run on every page load. The mode is the modal's own sticky preference, shared with Aged Care.
     */
    const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
    const [calendarYearProjections, setCalendarYearProjections] = useState<Record<string, unknown>[] | null>(null);
    useEffect(() => {
        if (modalPeriodMode !== 'calendar_year') { setCalendarYearProjections(null); return undefined; }
        if (!showProjectionsModal || !financials) return undefined;
        let cancelled = false;
        setCalendarYearProjections(null);
        calculationApi.runProjections(
            factFind,
            { projectionPeriodMode: 'calendar_year' },
            supportAtHomeCosts,
            builderLegacyYears,
            { debug: false },
        ).then((proj) => { if (!cancelled) setCalendarYearProjections(proj); });
        return () => { cancelled = true; };
    }, [showProjectionsModal, modalPeriodMode, factFind, financials, supportAtHomeCosts, builderLegacyYears]);

    useEffect(() => {
        if (!financials) { setProjections(null); return undefined; }

        let cancelled = false;
        // Run projections with factFind and Support at Home costs
        calculationApi.runProjections(factFind, {}, supportAtHomeCosts, builderLegacyYears, { debug: false }).then((proj) => {
            if (!cancelled) setProjections(proj);
        });
        return () => { cancelled = true; };
    }, [factFind, financials, outOfPocket, builderLegacyYears]);

    // Calculate projected legacy from projections
    const projectedLegacy = useMemo<number>(() => {
        if (!projections || projections.length === 0) return Number(financials?.netWorth) || 0;
        const lastYear = projections[projections.length - 1];
        return Number(lastYear.netWorth) || 0;
    }, [projections, financials]);

    const formatCurrency = (value: number | string | null | undefined): string => {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(value) || 0);
    };

    const handleServiceQuantityChange = (serviceId: string, quantity: number, frequency: string | undefined): void => {
        planTouchedThisVisit.current = true;
        clearCurrentServicePlanMeta();
        // Round to nearest integer (only whole hours/blocks allowed)
        const wholeNumber = Math.round(Math.max(0, quantity));

        // Find service details for dynamic messaging. Uses a for-of (rather than .forEach) so the
        // `serviceDetails` assignment below stays visible to TS's control-flow narrowing at the read
        // site further down — narrowing a `let` doesn't survive a reassignment inside a nested closure.
        let serviceDetails: { name: string; category: string; unit: string; medianPrice: number } | null = null;
        for (const category of Object.values(SUPPORT_AT_HOME_SERVICES)) {
            const service = category.services.find(s => s.id === serviceId);
            if (service) {
                serviceDetails = {
                    name: service.name,
                    category: category.label,
                    unit: service.unit,
                    medianPrice: service.medianPrice
                };
            }
        }

        setSelectedServices(prev => ({
            ...prev,
            [activeTab]: {
                ...prev[activeTab],
                [serviceId]: { quantity: wholeNumber, frequency: frequency || 'monthly' }
            }
        }));

        // Track last changed service for dynamic messaging
        if (wholeNumber > 0 && serviceDetails) {
            setLastChangedService({
                ...serviceDetails,
                serviceId,
                quantity: wholeNumber,
                frequency: frequency || 'monthly',
                activeTab
            });
        } else if (wholeNumber === 0) {
            // Service removed
            setLastChangedService(null);
        }
    };

    const handleCopyServices = (): void => {
        // Copy partner's services to client or vice versa
        const sourceServices = selectedServices[activeTab === 'client' ? 'partner' : 'client'];
        setSelectedServices(prev => ({
            ...prev,
            [activeTab]: { ...sourceServices }
        }));
    };

    // Filter services based on search with AI logic (keywords)
    const filteredServices = useMemo(() => {
        if (!searchQuery.trim()) return SUPPORT_AT_HOME_SERVICES;

        const query = searchQuery.toLowerCase();
        const filtered: Partial<typeof SUPPORT_AT_HOME_SERVICES> = {};

        (Object.entries(SUPPORT_AT_HOME_SERVICES) as [keyof typeof SUPPORT_AT_HOME_SERVICES, typeof SUPPORT_AT_HOME_SERVICES[keyof typeof SUPPORT_AT_HOME_SERVICES]][]).forEach(([categoryKey, category]) => {
            const matchingServices = category.services.filter(service =>
                // Match against service name
                service.name.toLowerCase().includes(query) ||
                // Match against keywords for natural language search
                (service.keywords && service.keywords.some((keyword: string) => keyword.toLowerCase().includes(query))) ||
                // Match against description for broader search
                (service.description && service.description.toLowerCase().includes(query))
            );
            if (matchingServices.length > 0) {
                filtered[categoryKey] = { ...category, services: matchingServices };
            }
        });

        return filtered;
    }, [searchQuery]);

    // Calculate current household cashflow for comparison
    const currentCashflow = Number(financials?.cashFlow) || 0;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Insight Panel */}
            <ServiceBuilderInsightPanel
                factFind={factFind}
                selectedServices={selectedServices}
                activeTab={activeTab}
                serviceCosts={serviceCosts}
                combinedCosts={combinedCosts}
                contributionRates={contributionRates}
                clientBudget={clientBudget}
                partnerBudget={partnerBudget}
                totalBudget={totalBudget}
                currentCashflow={currentCashflow}
                projectedLegacy={projectedLegacy}
                isPartnered={isPartnered}
                hasLinkedAgedCareStrategy={hasLinkedAgedCareStrategy}
                lastChangedService={lastChangedService}
            />
            
            {/* Left Column - Services */}
            <div className="lg:col-span-2 space-y-6">
                {/* The builder's coaching, inline (see ServiceBuilderCoach.tsx) — FIRST, so the
                    narrative reads top to bottom: what this tab is, then the financial position,
                    then the services (owner ordering, 2026-08-25). */}
                <ServiceBuilderIntro
                    independenceRate={contributionRates.independence || 0}
                    everydayRate={contributionRates.everydayLiving || 0}
                    isPartnered={isPartnered}
                    clientName={factFind?.client?.firstName || 'Client'}
                    partnerName={factFind?.partner?.firstName || 'Partner'}
                    clientBudget={clientBudget}
                    partnerBudget={partnerBudget}
                    hasLinkedAgedCareStrategy={hasLinkedAgedCareStrategy}
                    hasSelections={Object.keys(selectedServices.client || {}).length > 0 || Object.keys(selectedServices.partner || {}).length > 0}
                />

                {/*
                  * WHEN SUPPORT STARTS — a property of this plan, so it is asked here.
                  *
                  * It used to sit on Optimise Your Plan, which put a care fact among the financial
                  * levers one screen after the place it belongs. Optimise reads the value and links
                  * back here rather than asking again.
                  */}
                {setSupportStartDate && (
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm mb-6">
                        <div className="flex flex-col md:flex-row md:items-end gap-4">
                            <div className="flex-1">
                                <label htmlFor="sah-support-start" className="block text-lg font-bold text-gray-800">
                                    When will support services start?
                                </label>
                                <p className="text-sm text-gray-600 mt-1">
                                    Leave this blank to start from today — the right choice if services are already
                                    running, or if you&apos;re not sure yet. Choose a future date and no service costs
                                    are counted until that day.
                                </p>
                            </div>
                            <div>
                                <input
                                    id="sah-support-start"
                                    type="date"
                                    value={supportStartDate}
                                    onChange={(e) => setSupportStartDate(e.target.value)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                />
                                {supportStartDate !== '' && supportStartDate < new Date().toISOString().slice(0, 10) && (
                                    <p className="text-xs text-amber-700 mt-1">
                                        A past date models as “already receiving support”.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Strategy Selector */}
                {(availableHomeCareStrategies.length > 0 || availableAgedCareStrategies.length > 0) && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg p-4 mb-6 shadow-sm">
                        <label className="block text-sm text-gray-700 mb-2">
                            Select Financial Position:
                        </label>
                        <select
                            value={selectedStrategyId}
                            onChange={(e) => {
                                const nextId = e.target.value;
                                setSelectedStrategyId(nextId);
                                if (nextId === 'baseline') {
                                    setActiveHomeCareStrategyId(null);
                                    // Baseline means baseline: drop the aged-care link this dropdown's
                                    // earlier selection wrote, or the resolver keeps the linked
                                    // financials and the switch appears to do nothing.
                                    clearManualAgedCareLink();
                                } else {
                                    setActiveHomeCareStrategyId(nextId);
                                }
                                /*
                                 * Selecting a saved plan LOADS its services (owner-caught,
                                 * 2026-08-25: picking "Service A" changed only the invisible pricing
                                 * basis and loaded nothing — failing every reasonable expectation of
                                 * a dropdown full of the user's own plan names). Only when the
                                 * strategy actually carries services: a financial-only strategy
                                 * leaves the grid untouched, and Baseline changes pricing only.
                                 */
                                {
                                    const parsed = parseServiceBuilderSelectionId(nextId);
                                    if (parsed.kind === 'home_care') {
                                        const strategy = availableHomeCareStrategies.find((s) => s.id === parsed.rawId);
                                        const services = strategy?.inputs?.services as
                                            | { client?: Record<string, unknown>; partner?: Record<string, unknown> }
                                            | Record<string, unknown>
                                            | undefined;
                                        if (services && Object.keys(services).length > 0) {
                                            const couple = services as { client?: Record<string, unknown>; partner?: Record<string, unknown> };
                                            const normalised = (couple.client || couple.partner)
                                                ? { client: couple.client || {}, partner: couple.partner || {} }
                                                : { client: services as Record<string, unknown>, partner: {} };
                                            planTouchedThisVisit.current = true;
                                            setSelectedServices(normalised as never);
                                            setPlanName(String(strategy?.name || ''));
                                            setCurrentServicePlan(normalised as never, String(strategy?.name || '') || null);
                                        }
                                    }
                                }
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                        >
                            <option value="baseline">Baseline (No strategies applied)</option>
                            {availableHomeCareStrategies.length > 0 && (
                                <optgroup label="Support at Home (Optimise Your Plan)">
                                    {availableHomeCareStrategies.map((strategy) => {
                                        const contribution = Number(strategy.outputs?.annualContribution) || 0;
                                        const servicesInput = strategy.inputs?.services as SavedStrategyServicesShape | undefined;
                                        const hasServices = servicesInput && (
                                            (servicesInput.serviceDistribution &&
                                             ((servicesInput.serviceDistribution.clinical ?? 0) > 0 ||
                                              (servicesInput.serviceDistribution.independence ?? 0) > 0 ||
                                              (servicesInput.serviceDistribution.everyday ?? 0) > 0)) ||
                                            (servicesInput.services && Object.keys(servicesInput.services).length > 0)
                                        );
                                        return (
                                            <option
                                                key={strategy.id}
                                                value={formatServiceBuilderSelectionId('home_care', strategy.id)}
                                            >
                                                {formatHomeCareStrategyLabel(strategy)}
                                                {contribution > 0 ? (
                                                    hasServices
                                                        ? ` (${formatCurrency(contribution)} p.a. Support at Home contribution)`
                                                        : ` (Est. ${formatCurrency(contribution)} p.a. Support at Home contribution)`
                                                ) : ''}
                                            </option>
                                        );
                                    })}
                                </optgroup>
                            )}
                            {availableAgedCareStrategies.length > 0 && (
                                <optgroup label="Aged Care (saved scenarios)">
                                    {availableAgedCareStrategies.map((strategy) => {
                                        const agedCareCost = Number(strategy.outputs?.agedCareCost) || 0;
                                        return (
                                            <option
                                                key={strategy.id}
                                                value={formatServiceBuilderSelectionId('aged_care', strategy.id)}
                                            >
                                                {formatAgedCareStrategyLabel(strategy)}
                                                {agedCareCost > 0
                                                    ? ` (${formatCurrency(agedCareCost)} p.a. aged care)`
                                                    : ''}
                                            </option>
                                        );
                                    })}
                                </optgroup>
                            )}
                        </select>
                        <p className="text-xs text-gray-600 mt-2">
                            {selectedStrategyId === 'baseline'
                                ? 'Using your baseline financial position without any optimisation strategies.'
                                : serviceBuilderPosition.kind === 'aged_care'
                                    ? `Using household financials from your saved aged care scenario "${serviceBuilderPosition.strategy?.name || 'selected scenario'}". Support at Home contribution rates reflect the linked residential care context.`
                                    : `Using financial position from "${serviceBuilderPosition.strategy?.name || 'selected strategy'}". This affects your cashflow and contribution calculations.`
                            }
                        </p>
                    </div>
                )}
                
                {/* Search Bar */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search services..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                
                {/* Budget Overview */}
                <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                    {isPartnered ? (
                        <div>
                            <h3 className="text-xl font-bold mb-4">
                                <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-teal-500 bg-clip-text text-transparent">Available Budget</span>
                            </h3>
                            {/* Tabs for couples */}
                            <div className="flex gap-2 mb-4 border-b border-gray-200">
                                <button
                                    onClick={() => setActiveTab('client')}
                                    className={`px-4 py-2 font-semibold transition-colors ${
                                        activeTab === 'client' 
                                            ? 'text-blue-600 border-b-2 border-blue-600' 
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {factFind?.client?.firstName || 'Client'}
                                </button>
                                <button
                                    onClick={() => setActiveTab('partner')}
                                    className={`px-4 py-2 font-semibold transition-colors ${
                                        activeTab === 'partner' 
                                            ? 'text-blue-600 border-b-2 border-blue-600' 
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {factFind?.partner?.firstName || 'Partner'}
                                </button>
                            </div>
                            {hasLinkedAgedCareStrategy && activeTab === 'client' && (
                                <div className="mb-3 p-2 bg-white border border-slate-200/70 border-l-4 border-l-amber-500 rounded-lg text-xs text-amber-800 shadow-sm">
                                    {factFind?.client?.firstName || 'Client'}&apos;s budget is not available—they are in residential aged care (linked strategy). Switch to the partner tab to allocate their Support at Home budget.
                                </div>
                            )}
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-600">Package: {formatCurrency(activeTab === 'client' ? clientBudget : partnerBudget)}/year</p>
                                <div className="text-right">
                                    <div className="text-2xl font-bold text-gray-900">{formatCurrency(activeTab === 'client' ? clientBudget : partnerBudget)}</div>
                                    <div className="text-xs text-gray-500">Total Available</div>
                                    {Object.keys(selectedServices[activeTab] || {}).length > 0 && (
                                        <div className="mt-1">
                                            <div className="text-xs text-gray-600">Services Selected:</div>
                                            <div className="text-sm font-semibold text-gray-900">{formatCurrency(serviceCosts.total)}</div>
                                            <div className="text-xs text-gray-600 mt-1">Remaining:</div>
                                            <div className={`text-sm font-semibold ${(currentBudget - currentServicesUsed) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                {formatCurrency(currentBudget - currentServicesUsed)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {Object.keys(selectedServices[activeTab] || {}).length > 0 && serviceCosts.contribution.total > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-600">Your Contribution (within package):</span>
                                        <span className="text-lg font-bold text-blue-600">{formatCurrency(activeOutOfPocket?.fundedContribution || 0)}</span>
                                    </div>
                                    {(activeOutOfPocket?.privateExcess || 0) > 0 && (
                                        <div className="mt-1 flex items-center justify-between">
                                            <span className="text-xs text-gray-600">Privately funded (above package):</span>
                                            <span className="text-lg font-bold text-slate-900">{formatCurrency(activeOutOfPocket?.privateExcess || 0)}</span>
                                        </div>
                                    )}
                                    <ContributionCashflowNote
                                        annualContribution={outOfPocket.combined.total}
                                        currentCashflow={currentCashflow}
                                    />
                                </div>
                            )}
                            {/* Copy button - show always when on partner tab, if client has services to copy */}
                            {activeTab === 'partner' && Object.keys(selectedServices.client || {}).length > 0 && (
                                <div className="mt-3">
                                    <button
                                        onClick={handleCopyServices}
                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                                    >
                                        <Copy size={12} /> Copy {factFind?.client?.firstName || 'Client'}'s Services
                                    </button>
                                </div>
                            )}
                            {/* Copy button - show always when on client tab, if partner has services to copy */}
                            {activeTab === 'client' && Object.keys(selectedServices.partner || {}).length > 0 && (
                                <div className="mt-3">
                                    <button
                                        onClick={handleCopyServices}
                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                                    >
                                        <Copy size={12} /> Copy {factFind?.partner?.firstName || 'Partner'}'s Services
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-xl font-bold mb-2">
                                    <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-teal-500 bg-clip-text text-transparent">Available Budget</span>
                                </h3>
                                <p className="text-sm text-gray-600">{formatCurrency(clientBudget)}/year</p>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-gray-900">{formatCurrency(clientBudget)}</div>
                                <div className="text-xs text-gray-500">Total Available</div>
                            </div>
                        </div>
                    )}
                    {Object.keys(isPartnered ? selectedServices[activeTab] : (selectedServices.client || {})).length > 0 && !isPartnered && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="grid grid-cols-2 gap-4 text-xs">
                                <div>
                                    <span className="text-gray-600">Services Selected:</span>
                                    <span className="ml-2 font-semibold text-gray-900">{formatCurrency(serviceCosts.total)}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Remaining:</span>
                                    <span className={`ml-2 font-semibold ${(currentBudget - currentServicesUsed) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {formatCurrency(currentBudget - currentServicesUsed)}
                                    </span>
                                </div>
                            </div>
                            {serviceCosts.contribution.total > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-600">Your Contribution (within package):</span>
                                        <span className="text-lg font-bold text-blue-600">{formatCurrency(outOfPocket.client.fundedContribution)}</span>
                                    </div>
                                    {outOfPocket.client.privateExcess > 0 && (
                                        <div className="mt-1 flex items-center justify-between">
                                            <span className="text-xs text-gray-600">Privately funded (above package):</span>
                                            <span className="text-lg font-bold text-slate-900">{formatCurrency(outOfPocket.client.privateExcess)}</span>
                                        </div>
                                    )}
                                    <ContributionCashflowNote
                                        annualContribution={outOfPocket.combined.total}
                                        currentCashflow={currentCashflow}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            

            
                {/* Service Selection by Category */}
                {Object.keys(filteredServices).length === 0 && searchQuery.trim() ? (
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm text-center">
                        <p className="text-gray-600">No services found matching "{searchQuery}"</p>
                    </div>
                ) : (
                    Object.entries(filteredServices).map(([categoryKey, category]) => (
                        <div key={categoryKey} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-xl font-bold text-gray-900">{category.label}</h3>
                                    {categoryKey === 'independence' && (
                                        <span className="text-lg font-bold text-blue-600">
                                            {((contributionRates.independence || 0) * 100).toFixed(0)}%
                                        </span>
                                    )}
                                    {categoryKey === 'everyday' && (
                                        <span className="text-lg font-bold text-blue-600">
                                            {((contributionRates.everydayLiving || 0) * 100).toFixed(0)}%
                                        </span>
                                    )}
                                    {categoryKey === 'clinical' && <span className="text-lg font-bold text-blue-600">0%</span>}
                                </div>
                                {(categoryKey === 'clinical' && serviceCosts.totalClinical > 0) ||
                                 (categoryKey === 'independence' && serviceCosts.totalIndependence > 0) ||
                                 (categoryKey === 'everyday' && serviceCosts.totalEveryday > 0) ? (
                                    <div className="text-right">
                                        <div className="text-sm text-gray-600">Selected:</div>
                                        <div className="text-lg font-bold text-gray-900">
                                            {formatCurrency(
                                                categoryKey === 'clinical' ? serviceCosts.totalClinical :
                                                categoryKey === 'independence' ? serviceCosts.totalIndependence :
                                                serviceCosts.totalEveryday
                                            )}
                                        </div>
                                        {categoryKey !== 'clinical' && serviceCosts.contribution[categoryKey] > 0 && (
                                            <div className="text-xs text-gray-500 mt-1">
                                                Your contribution: {formatCurrency(serviceCosts.contribution[categoryKey])}
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                            
                            <div className="space-y-3">
                                {category.services.map(service => {
                                    const servicesForTab = isPartnered ? selectedServices[activeTab] : (selectedServices.client || {});
                                    const currentData = servicesForTab[service.id] || { quantity: 0, frequency: 'monthly' };
                                    const monthlyQuantity = convertToMonthly(currentData.quantity, currentData.frequency);
                                    const monthlyCost = service.medianPrice * monthlyQuantity;
                                    const isSelected = Number(currentData.quantity || 0) > 0;
                                    return (
                                        <div
                                            key={service.id}
                                            className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg border-2 ${isSelected ? 'border-blue-500' : 'border-gray-200'}`}
                                        >
                                            <div className="flex-1 min-w-0 mr-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-gray-900">{service.name}</span>
                                                    <InfoIcon text={`${service.description || ''}\n\nIndicative price range: ${formatCurrency(service.range.lower)} - ${formatCurrency(service.range.upper)} per ${service.unit.toLowerCase()}`} />
                                                </div>
                                                <div className="text-sm text-gray-600 mt-1">
                                                    Indicative price: {formatCurrency(service.medianPrice)} per {service.unit.toLowerCase()}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0 w-[236px] justify-end">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={currentData.quantity ?? ''}
                                                    onChange={(e) => handleServiceQuantityChange(service.id, parseFloat(e.target.value) || 0, currentData.frequency)}
                                                    placeholder="0"
                                                    className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right tabular-nums"
                                                />
                                                <select
                                                    value={currentData.frequency}
                                                    onChange={(e) => handleServiceQuantityChange(service.id, Number(currentData.quantity) || 0, e.target.value)}
                                                    className="w-[72px] px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm flex-shrink-0"
                                                >
                                                    <option value="daily">/day</option>
                                                    <option value="weekly">/week</option>
                                                    <option value="monthly">/mo</option>
                                                </select>
                                                <span className="text-sm font-semibold text-gray-900 w-20 text-right tabular-nums">
                                                    {isSelected ? `${formatCurrency(monthlyCost)}/mo` : '—'}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                )}
            
            </div>
            
            {/* Right Column - Service Budget & Impact Panel */}
            <div className="lg:col-span-1 space-y-6 transition-all duration-300">
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg sticky top-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <TrendingUp className="w-6 h-6 text-teal-600" />
                        <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-teal-500 bg-clip-text text-transparent">Service Budget</span>
                    </h3>
                    
                    {!isPartnered && Object.keys(selectedServices.client || {}).length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Info size={32} className="mx-auto mb-3 text-gray-400" />
                            <p className="text-sm">Select services to see the impact</p>
                        </div>
                    ) : isPartnered && Object.keys(selectedServices.client || {}).length === 0 && Object.keys(selectedServices.partner || {}).length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Info size={32} className="mx-auto mb-3 text-gray-400" />
                            <p className="text-sm">Select services to see the impact</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Combined Budget Usage */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">Combined Budget</h4>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Total Combined Budget</span>
                                        <span className="text-lg font-bold text-gray-900">{formatCurrency(totalBudget)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Your Contribution (within package)</span>
                                        <span className="text-lg font-bold text-blue-600">{formatCurrency(outOfPocket.combined.fundedContribution)}</span>
                                    </div>
                                    {outOfPocket.combined.privateExcess > 0 && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Privately funded (above package)</span>
                                            <span className="text-lg font-bold text-slate-900">{formatCurrency(outOfPocket.combined.privateExcess)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Remaining Budget</span>
                                        <span className={`text-lg font-bold ${(totalBudget - (isPartnered ? combinedCosts.total : serviceCosts.total)) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                            {formatCurrency(totalBudget - (isPartnered ? combinedCosts.total : serviceCosts.total))}
                                        </span>
                                    </div>
                                    <div className="pt-2">
                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div 
                                                className={`h-full transition-all duration-500 ${(totalBudget - (isPartnered ? combinedCosts.total : serviceCosts.total)) < 0 ? 'bg-red-500' : 'bg-green-500'}`}
                                                style={{ width: `${Math.min(100, Math.max(0, totalBudget > 0 ? ((isPartnered ? combinedCosts.total : serviceCosts.total) / totalBudget) * 100 : 0))}%` }}
                                            />
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {(totalBudget > 0 ? (((isPartnered ? combinedCosts.total : serviceCosts.total) / totalBudget) * 100) : 0).toFixed(1)}% of budget used
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Individual Budget Mini-Box (for couples only) */}
                            {isPartnered && (
                                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Individual Budget - {activeTab === 'client' ? factFind?.client?.firstName || 'Client' : factFind?.partner?.firstName || 'Partner'}</h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-600">Used:</span>
                                            <span className="font-semibold text-gray-900">{formatCurrency(serviceCosts.total)}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-600">Remaining:</span>
                                            <span className={`font-semibold ${(currentBudget - currentServicesUsed) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                {formatCurrency(currentBudget - currentServicesUsed)}
                                            </span>
                                        </div>
                                        <div className="pt-1">
                                            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                <div 
                                                    className={`h-full transition-all duration-500 ${(currentBudget - currentServicesUsed) < 0 ? 'bg-red-500' : 'bg-green-500'}`}
                                                    style={{ width: `${Math.min(100, Math.max(0, currentBudget > 0 ? (currentServicesUsed / currentBudget) * 100 : 0))}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Over budget — the coaching sits HERE, beside the red remaining figure
                                it explains: the sticky panel is what the user actually sees while
                                deep in the service lists (owner placement, 2026-08-25). */}
                            {(currentBudget - currentServicesUsed) < 0 && (
                                <OverBudgetHelp
                                    overBy={Math.abs(currentBudget - currentServicesUsed)}
                                    personName={isPartnered ? ((activeTab === 'client' ? factFind?.client?.firstName : factFind?.partner?.firstName) || undefined) : undefined}
                                    isPartnered={isPartnered}
                                    otherName={(activeTab === 'client' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'your partner'}
                                    otherHasRoom={
                                        isPartnered &&
                                        (activeTab === 'client'
                                            ? partnerBudget > 0
                                            : clientBudget > 0)
                                    }
                                />
                            )}

                            {/* Cashflow Impact */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">Cashflow Impact</h4>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Current Cashflow</span>
                                        <span className="text-lg font-bold text-gray-900">{formatCurrency(currentCashflow)}/year</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Less: Contribution (within package)</span>
                                        <span className="text-lg font-bold text-red-600">-{formatCurrency(outOfPocket.combined.fundedContribution)}</span>
                                    </div>
                                    {outOfPocket.combined.privateExcess > 0 && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Less: Private services (above package)</span>
                                            <span className="text-lg font-bold text-red-600">-{formatCurrency(outOfPocket.combined.privateExcess)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center pt-2 border-t-2 border-gray-300">
                                        <span className="text-sm font-bold text-gray-900">Remaining Cashflow</span>
                                        <span className={`text-xl font-bold ${(currentCashflow - outOfPocket.combined.total) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {formatCurrency(currentCashflow - outOfPocket.combined.total)}/year
                                        </span>
                                    </div>
                                    {projections && projections.length > 0 && (
                                        <div className="flex justify-end pt-1">
                                            <button
                                                type="button"
                                                onClick={() => setShowProjectionsModal(true)}
                                                className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800"
                                            >
                                                <Eye size={15} /> View projections
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Cashflow Shortfall with Legacy Impact (shown only when in deficit) */}
                            {(currentCashflow - outOfPocket.combined.total) < 0 && (
                                <div className="space-y-4">
                                    <div className="bg-white border border-slate-200/70 border-l-4 border-amber-400 p-4 rounded-r-lg">
                                        <div className="flex items-start gap-3">
                                            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="font-bold text-yellow-900 mb-1">Cashflow Shortfall</p>
                                                <p className="text-sm text-yellow-800">
                                                    {/* The aged care module's walk-in runway rule, reused verbatim
                                                        (buildWalkInGapLine): always "roughly", always deferring to the
                                                        projections, never inventing a runway with no savings. */}
                                                    {buildWalkInGapLine({
                                                        cashFlowBeforeCare: currentCashflow,
                                                        yearlyCare: outOfPocket.combined.total,
                                                        accessibleSavings: accessibleSavingsOf(factFind),
                                                    }).text}{' '}
                                                    {projections && projections.length > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowProjectionsModal(true)}
                                                            className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                                                        >
                                                            See the year-by-year projections ›
                                                        </button>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* The estate at the horizon (only shown when a shortfall exists). Estate,
                                        not "legacy" — plain words on new surfaces (owner, 2026-08-25) — and ONE
                                        figure: the old box showed the same number under two labels. */}
                                    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                                        <div className="mb-2 flex items-center justify-between gap-2">
                                            <h4 className="text-sm font-semibold text-gray-700">What would be left (estate)</h4>
                                            <label className="flex items-center gap-1.5 text-xs text-gray-600">
                                                after
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={30}
                                                    value={builderLegacyYears}
                                                    onChange={(e) => setBuilderLegacyYears(Math.max(1, Math.min(30, Number(e.target.value) || 5)))}
                                                    className="w-14 rounded-lg border border-gray-300 px-2 py-1 text-sm"
                                                />
                                                years
                                            </label>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-bold text-gray-900">Projected estate</span>
                                            <span className={`text-xl font-bold ${projectedLegacy >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {formatCurrency(projectedLegacy)}
                                            </span>
                                        </div>
                                        <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                                            Everything owned at that point, after the service costs and living expenses of
                                            each year in between — the projections show the year-by-year path.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Save Plan Section - Inline like Optimise Your Plan */}
            <div className="col-span-full pt-6 border-t border-gray-200 mt-6">
                {(isPartnered ? (Object.keys(selectedServices.client || {}).length > 0 || Object.keys(selectedServices.partner || {}).length > 0) : Object.keys(selectedServices.client || {}).length > 0) && (
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm mb-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">Save Service Plan</h3>
                        <div className="space-y-4">
                            <div className="flex items-center gap-4">
                                <input
                                    type="text"
                                    placeholder="Enter plan name (e.g., Balanced Care Plan)"
                                    value={planName}
                                    onChange={(e) => setPlanName(e.target.value)}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <button
                                    onClick={() => {
                                        if (!planName.trim()) {
                                            notify('Please enter a plan name');
                                            return;
                                        }
                                        // A plan with no services prices nothing, and a saved one
                                        // kept surfacing in the Optimise picker only to snap back
                                        // to "no plan" (owner-caught, 2026-09-13). Say so here.
                                        if (!storedPlanHasServices(selectedServices)) {
                                            notify('Add at least one service (with a quantity) before saving the plan');
                                            return;
                                        }
                                        
                                        // Get existing saved strategies or initialize
                                        const savedStrategies = JSON.parse(sessionStorage.getItem('careCompanionStrategies') || '[]');
                                        
                                        /*
                                         * The state is ALREADY {client, partner} for everyone — see its
                                         * initialiser. This used to wrap it again for a single client
                                         * (`{ client: selectedServices, partner: null }`), burying the
                                         * services one level too deep: every single client's saved plan
                                         * priced as empty, reopened to a blank grid, and the Optimise
                                         * picker snapped back to "no plan" on selecting it (owner-caught,
                                         * 2026-09-13). The record is now built beside its reader and
                                         * round-trip tested — servicePlanStorage.test.ts.
                                         */
                                        const strategy = buildServicePlanRecord({
                                            name: planName,
                                            services: selectedServices,
                                            outputs: {
                                                totalCost: isPartnered ? combinedCosts.total : serviceCosts.total,
                                                contribution: isPartnered ? combinedCosts.contribution.total : serviceCosts.contribution.total,
                                                legacyEstimate: projectedLegacy
                                            },
                                        });
                                        
                                        // Add to saved strategies
                                        savedStrategies.push(strategy);
                                        sessionStorage.setItem('careCompanionStrategies', JSON.stringify(savedStrategies));
                                        // The selections on the grid ARE the plan just saved, so the
                                        // Optimise picker names it rather than calling it "not saved".
                                        setCurrentServicePlan(selectedServices as never, planName.trim());
                                        
                                        // Also save to factFind for backward compatibility
                                        setFactFind(prev => ({
                                            ...prev,
                                            servicePlan: {
                                                ...strategy.inputs,
                                                costs: isPartnered ? combinedCosts : serviceCosts,
                                                savedAt: new Date().toLocaleString(),
                                                clientName: factFind?.client?.firstName || 'Client',
                                                partnerName: isPartnered ? factFind?.partner?.firstName : null
                                            }
                                        }));
                                        
                                        setPlanName('');
                                        // The list below refreshes immediately — see savedPlans.
                                        setSavedPlans(listSavedServicePlans());
                                        setActivePlanId(strategy.id);
                                        setShowServicePlanSavedModal(true); // Show next steps modal
                                    }}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                                >
                                    Save Plan
                                </button>
                            </div>

                        </div>
                    </div>
                )}

                {/*
                  * SAVED PLANS, VISIBLE HERE — same treatment as the Aged Care analyser's
                  * saved-strategies list, which lists them directly under its save box.
                  *
                  * Outside the "has services selected" gate above, deliberately: the point of the
                  * list is to REOPEN a plan, and a client returning to an empty grid is exactly who
                  * needs it. Gating it on current selections would hide it from them.
                  *
                  * Reopening loads the plan back into the grid AND makes it the current working
                  * plan, so the Optimise tab prices the same services the client is looking at.
                  * Doing one without the other is how the grid and the pricing come to disagree.
                  */}
                {savedPlans.length > 0 && (
                    <div className="rounded-r-lg border-l-4 border-blue-400 bg-blue-50 p-4">
                        <p className="mb-2 text-sm text-blue-700">
                            <strong>{savedPlans.length}</strong> service {savedPlans.length === 1 ? 'plan' : 'plans'} saved.
                        </p>
                        <div className="space-y-1">
                            {savedPlans.map((saved) => (
                                <div
                                    key={saved.id}
                                    className={`flex items-center justify-between rounded-lg px-2 py-1.5 text-xs transition-colors ${
                                        activePlanId === saved.id
                                            ? 'border border-blue-500 bg-white shadow-sm ring-2 ring-blue-500'
                                            : 'text-gray-600'
                                    }`}
                                >
                                    <span className="text-gray-600">{saved.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedServices(
                                                normaliseStoredServices(saved.plan) as unknown as ServiceBuilderSelections,
                                            );
                                            setCurrentServicePlan(saved.plan, saved.name);
                                            setActivePlanId(saved.id);
                                        }}
                                        className="font-semibold text-blue-600 underline hover:text-blue-800"
                                    >
                                        Reopen
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            
            {/* Navigation Buttons - Full Width */}
            <div className="col-span-full pt-6 border-t border-gray-200 mt-6 space-y-4">
                {/* Instructional Message */}
                <div className="bg-slate-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                    <p className="text-sm text-gray-700">
                        {/* The builder is no longer the last stop: Optimise follows it (owner
                            direction, 2026-08-27 — need before money). With a real plan built, the
                            contribution figures there are the client's own dollars rather than an
                            illustrative package's, which is the whole point of the reorder. */}
                        <strong>Next Steps:</strong> Save your plan, then head to <strong>Optimise Your Plan</strong> to see
                        whether these contribution rates can be lowered — now priced against the services you have actually
                        chosen. You can come back here any time if your provider quotes different fees.
                    </p>
                </div>
                
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                        >
                            <ChevronLeft size={20} /> Back
                        </button>
                    </div>
                    <div className="flex items-center gap-4">
                        {(isPartnered ? (Object.keys(selectedServices.client || {}).length > 0 || Object.keys(selectedServices.partner || {}).length > 0) : Object.keys(selectedServices.client || {}).length > 0) && (
                            <div className="text-sm text-gray-600">
                                {isPartnered 
                                    ? `${Object.keys(selectedServices.client || {}).length + Object.keys(selectedServices.partner || {}).length} service${Object.keys(selectedServices.client || {}).length + Object.keys(selectedServices.partner || {}).length > 1 ? 's' : ''} selected`
                                    : `${Object.keys(selectedServices.client || {}).length} service${Object.keys(selectedServices.client || {}).length > 1 ? 's' : ''} selected`}
                            </div>
                        )}
                        {/* Says where it goes. `onNext` now opens Optimise, not the Dashboard, and a
                            button labelled for the wrong destination is the same defect the page's
                            Next button had. */}
                        <button
                            onClick={onNext}
                            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                        >
                            Next: Optimise Your Plan <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* The shared engine view (charts, monthly narrative, per-figure tooltips) over the SAME
                rows the Service Budget's cashflow/legacy figures come from — the household projection
                charged with the contribution cost of the services chosen in this builder. */}
            {showProjectionsModal && projections && projections.length > 0 && (
                <ProjectionsModal
                    onClose={() => setShowProjectionsModal(false)}
                    projections={(modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : projections) as never}
                    onPeriodModeChange={setModalPeriodMode}
                    isReloading={modalPeriodMode === 'calendar_year' && !calendarYearProjections}
                    factFind={factFind as never}
                    initialFinancials={(financials ?? null) as never}
                    currentFinancials={(financials ?? null) as never}
                    agedCareCosts={{ totalDailyCost: (isPartnered ? combinedCosts.contribution.total : serviceCosts.contribution.total) / 365 } as never}
                    costLabel="Support at Home Costs"
                />
            )}
        </div>
    );
};

// Simple InfoIcon Component
const InfoIcon = ({ text }: { text: string }) => {
    const [show, setShow] = useState(false);
    return (
        <span className="relative inline-block ml-1 align-middle" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
            <HelpCircle size={16} className="text-gray-500 cursor-pointer" />
            {show && (
                <div className="absolute left-0 bottom-full mb-2 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-50 whitespace-pre-line" style={{ width: '320px' }}>
                    {text}
                </div>
            )}
        </span>
    );
};


export {
    SupportDownsizeStrategyAllocator,
    ReverseMortgageStrategyAllocator,
    HEASStrategyAllocator,
    RenovateStrategyAllocator,
    UpgradeStrategyAllocator
} from './SupportAtHomePropertyAllocators.tsx';
export default ServiceBuilderSection;
